import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';

const AuditTrail = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        authFetch('/api/audit')
            .then(res => res.json())
            .then(data => {
                setLogs(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    }, [authFetch]);

    const getActionColor = (action) => {
        switch (action) {
            case 'Purchase':
            case 'Add':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'Transfer':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'Edit':
            case 'Delete':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const renderJsonDetails = (jsonObj) => {
        if (!jsonObj) return <span className="text-gray-400 italic">{t('None')}</span>;

        return (
            <div className="text-xs bg-gray-50 border border-gray-200 rounded p-2 max-h-32 overflow-y-auto w-full max-w-xs space-y-1 font-mono">
                {Object.entries(jsonObj).map(([key, value]) => (
                    <div key={key} className="flex justify-between border-b border-gray-100 last:border-0 pb-1 last:pb-0">
                        <span className="text-gray-500 mr-2">{key}:</span>
                        <span className="text-gray-900 font-bold truncate">{String(value)}</span>
                    </div>
                ))}
            </div>
        );
    };

    const columns = [
        { header: t('ID'), accessor: 'id' },
        { header: t('User'), accessor: 'username', render: (row) => <span className="font-semibold text-gray-700">{row.username || t('System')}</span> },
        {
            header: t('Action'),
            accessor: 'action_type',
            render: (row) => (
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getActionColor(row.action_type)}`}>
                    {t(row.action_type)}
                </span>
            )
        },
        { header: t('Table Affected'), accessor: 'table_name', render: (row) => <span className="text-purple-600 font-mono text-sm">{row.table_name}</span> },
        { header: t('Record ID'), accessor: 'record_id' },
        { header: t('Before (Old Value)'), accessor: 'old_value', render: (row) => renderJsonDetails(row.old_value) },
        { header: t('After (New Value)'), accessor: 'new_value', render: (row) => renderJsonDetails(row.new_value) },
        { header: t('IP Address'), accessor: 'ip_address', render: (row) => <span className="text-gray-500 text-sm">{row.ip_address}</span> },
        { header: t('Timestamp'), accessor: 'logged_at', render: (row) => <span className="text-sm whitespace-nowrap">{new Date(row.logged_at).toLocaleString()}</span> }
    ];

    if (loading) return <div className="text-center mt-20 text-gray-500">{t('Loading secure audit logs...')}</div>;

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800 flex items-center">
                        <span className={`${t('dir') === 'rtl' ? 'ml-3' : 'mr-3'}`}>🛡️</span> {t('Security & Audit Trail')}
                    </h2>
                    <p className="text-gray-500 mt-2">{t('Immutable log of all critical actions performed in the system.')}</p>
                </div>
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-200 text-sm font-bold flex items-center shadow-sm">
                    <span className={`${t('dir') === 'rtl' ? 'ml-2' : 'mr-2'}`}>🔒</span> {t('Admin God-Mode Active')}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <DataTable columns={columns} data={logs} />
            </div>
        </div>
    );
};

export default AuditTrail;
